/**
 * @file: sessionTracking.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

/* eslint-disable @typescript-eslint/promise-function-async */

import { useAuthContext } from '@euler/app/flows/auth/context';
import { useLogout, userLoggedOut$ } from '@euler/app/flows/auth/hooks';
import { config } from '@euler/config';
import { CloudPush } from '@euler/lib/notification/CloudPush';
import {
  UserSessionInfo,
  areUserSessionsSame,
  session$,
} from '@euler/lib/session';
import { AuthProviderType } from '@euler/model/auth';
import { useServiceFactory } from '@euler/services/factory';
import { isNotNull, makeDebug, onErrorIgnore } from '@euler/utils';
import { useEffect, useRef } from 'react';
import {
  EMPTY,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  from,
  map,
  scan,
  switchMap,
} from 'rxjs';

type AuthStateInfo = { uid: string; provider?: AuthProviderType };
type AuthStateChange = [AuthStateInfo, AuthStateInfo?];
type SessionChange = [UserSessionInfo, UserSessionInfo?];

const debug = makeDebug('session:tracker', true);

export const sessionExpired$ = new Subject<void>();

export function useSessionTracker() {
  const authContext = useAuthContext();
  const { userService } = useServiceFactory();
  const isSessionExpiring = useRef(false);
  const logout = useLogout();
  useEffect(() => {
    const subscription = sessionExpired$.subscribe(async () => {
      if (isSessionExpiring.current || userLoggedOut$.value) return;
      isSessionExpiring.current = true;
      // silent — drop straight to the login screen rather than making the user
      // dismiss an alert before the app can do the only thing left to it
      debug('session expired, logging out');
      await logout();
      isSessionExpiring.current = false;
    });
    return () => subscription.unsubscribe();
  }, [logout]);

  useEffect(() => {
    const subscription = combineLatest([
      authContext.authState$.pipe(
        map(x =>
          x.isAuthenticated
            ? { uid: x.authenticatedUserInfo.user.uid, provider: x.provider }
            : x.isAuthenticated === undefined
            ? undefined
            : { uid: '' },
        ),
        filter(isNotNull),
        distinctUntilChanged((x, y) => x.uid === y.uid),
        scan<AuthStateChange['0'], AuthStateChange>(
          ([prev], curr) => [curr, prev],
          [] as any,
        ),
      ),
      session$().pipe(
        filter(isNotNull),
        distinctUntilChanged(areUserSessionsSame),
        scan<UserSessionInfo, SessionChange>(
          ([prev], curr) => [curr, prev],
          [] as any,
        ),
      ),
    ])
      .pipe(
        switchMap(
          ([[currAuthState, prevAuthState], [currSession, prevSession]]) => {
            if (!currSession) {
              debug('current session is nil');
              return EMPTY;
            }

            debug('auth.curr: %O, auth.prev: %O', currAuthState, prevAuthState);

            const sessionProps = {
              ...currSession.sessionProps,
              authProvider: currAuthState.provider,
            };
            if (prevAuthState === undefined) {
              if (currAuthState.uid) {
                if (prevSession) {
                  // user session props has been updated, for example,
                  // the user has changed notification authorization state
                  debug(
                    'user presence login for previous authenticated session: %s',
                    currSession.sessionId,
                  );
                  return from(
                    userService
                      .presenceLogin(sessionProps)
                      .then(() =>
                        config.cloudPush.enabled
                          ? CloudPush.bindAccount(currAuthState.uid)
                          : null,
                      )
                      .catch(onErrorIgnore),
                  );
                } else {
                  debug(
                    'user presence active for previous authenticated session: %s',
                    currSession.sessionId,
                  );
                  // user entered app in authenticated state
                  return from(
                    userService
                      .presenceActive(sessionProps)
                      .then(valid => {
                        if (valid) {
                          return config.cloudPush.enabled
                            ? CloudPush.bindAccount(currAuthState.uid)
                            : null;
                        }
                        return userService
                          .presenceLogin(sessionProps)
                          .then(() =>
                            config.cloudPush.enabled
                              ? CloudPush.bindAccount(currAuthState.uid)
                              : null,
                          )
                          .catch(onErrorIgnore);
                      })
                      .catch(onErrorIgnore),
                  );
                }
              }
              return EMPTY;
            } else if (currAuthState.uid) {
              debug(
                'user presence login for new authenticated session: %s',
                currSession.sessionId,
              );
              // after user has successfully logged in
              return from(
                userService
                  .presenceLogin(sessionProps)
                  .then(() =>
                    config.cloudPush.enabled
                      ? CloudPush.bindAccount(currAuthState.uid)
                      : null,
                  )
                  .catch(onErrorIgnore),
              );
            } else {
              // after user has logged out
              return config.cloudPush.enabled
                ? from(CloudPush.unbindAccount())
                : EMPTY;
            }
          },
        ),
      )
      .subscribe();
    return () => subscription.unsubscribe();
  }, [authContext.authState$, userService]);
}
